import {
  AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges,
  QueryList, ViewChild, ViewChildren, computed, inject, signal
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, of, take } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { CanalOrigemContatoEnum } from '../../shared/enum/canal-origem-contato.enum';
import { MensagemStatusEnum } from '../../shared/enum/mensagens/mensagem-status.enum';
import { MensagemTipoEnum } from '../../shared/enum/mensagens/mensagem-tipo.enum';
import { TipoPreviaMensagemEnum } from '../../shared/enum/tipo-previa-mensagem.enum';
import { AtendimentoModel } from '../../shared/model/atendimento/atendimento-model';
import { EnvioMensagemTemplate } from '../../shared/model/chat/mensagens/envio-mensagem-template.model';
import { MensagemModel } from '../../shared/model/chat/mensagens/mensagem-model';
import { TelefoneIdDataMensagem } from '../../shared/model/chat/telefone-id-data-mensagem.model';
import { Contato } from '../../shared/model/contato/contato.model';
import { NovaMensagemContato } from '../../shared/model/contato/nova-mensagem-contato.model';
import { UsuarioLogado } from '../../shared/model/usuario/usuario-logado.model';
import { AuthService } from '../../shared/service/auth.service';
import { ContatoService } from '../../shared/service/contato.service';
import { EnvioTemplateChatService } from '../../shared/service/envio-template-chat.service';
import { LembreteService } from '../../shared/service/lembrete.service';
import { MensagemService } from '../../shared/service/mensagens.service';
import { MethodsUtils } from '../../shared/util/methods.utils';
import { MsgService } from '../../shared/service/msg.service';
import { SocketService } from '../../shared/service/socket.service';
import { UsuarioService } from '../../shared/service/usuario.service';
import { SomTransicaoAudioService } from '../../shared/service/som-transicao-audio.service';
import { AlbumImagensComponent } from './album-imagens/album-imagens.component';
import { BalaoMensagemComponent } from './balao-mensagem/balao-mensagem.component';
import { ReacaoBalao } from './balao-mensagem/models/reacao-balao.model';
import { ConversaCabecalhoComponent } from './conversa-cabecalho/conversa-cabecalho.component';
import { ModalEditarContatoComponent } from '../../shared/components/redesign/modal-editar-contato/modal-editar-contato.component';
import { ConversaRodapeComponent } from './conversa-rodape/conversa-rodape.component';
import { EnvioArquivosChat } from './conversa-rodape/models/envio-arquivos-chat.model';
import { SelecaoTemplateComponent } from './selecao-template/selecao-template.component';
import { GaleriaMidiaComponent } from './galeria-midia/galeria-midia.component';
import { GaleriaMidiaData } from './galeria-midia/models/galeria-midia-data.model';
import { GaleriaMidiaResultado } from './galeria-midia/models/galeria-midia-resultado.model';
import { ItemGaleria } from './galeria-midia/models/item-galeria.model';
import { TipoItemGaleria } from './galeria-midia/models/tipo-item-galeria.model';
import { ModalAtendimentoComponent } from './modal-atendimento/modal-atendimento.component';
import { VisualizadorFigurinhaComponent } from './visualizador-figurinha/visualizador-figurinha.component';
import { VisualizadorFigurinhaData } from './visualizador-figurinha/models/visualizador-figurinha-data.model';
import { VisualizadorFigurinhaResultado } from './visualizador-figurinha/models/visualizador-figurinha-resultado.model';
import { ModalAtendimentoData } from './modal-atendimento/models/modal-atendimento-data.model';
import { PerfilContatoChatComponent } from './perfil-contato-chat/perfil-contato-chat.component';
import { PerfilContatoChatData } from './perfil-contato-chat/models/perfil-contato-chat-data.model';
import { CanalModel } from '../../shared/model/canal/canal-model';
import { AtendimentoTransferido } from './models/atendimento-transferido.model';
import { PerfilContatoChatResultado } from './perfil-contato-chat/models/perfil-contato-chat-resultado.model';
import { CLASSE_FONTE_CHAT } from '../utils/chat.constantes';
import { animacaoFade, animacaoSurgir } from '../utils/chat-animacoes.utils';
import { ItemConversa } from './models/item-conversa.model';
import { AlvoLocalizacaoChat } from './models/alvo-localizacao-chat.model';
import { ReacaoPendente } from './models/reacao-pendente.model';
import {
  CLASSES_DESTAQUE_CONVERSA, DURACAO_DESTAQUE_CONVERSA_MS, ESPERA_REACAO_MS, EXT_POR_TIPO, INTERVALO_ALBUM_MS, JANELA_INICIAL,
  JANELA_SESSAO_MS, LIMITES_ARQUIVO_MB, LIMITE_ENCOLHER_JANELA, MAX_PAGINAS_BUSCA_MENSAGEM, PASSO_JANELA, TELAS_PRE_CARGA,
  TIPO_GALERIA_POR_EXTENSAO, TOLERANCIA_FIM_PX
} from './utils/chat-conversa.constantes';

@Component({
  selector: 'app-chat-conversa',
  standalone: true,
  imports: [ConversaCabecalhoComponent, ConversaRodapeComponent, BalaoMensagemComponent, AlbumImagensComponent, SelecaoTemplateComponent],
  templateUrl: './chat-conversa.component.html',
  styleUrl: './chat-conversa.component.css',
  animations: [animacaoFade, animacaoSurgir]
})
export class ChatConversaComponent implements OnChanges, AfterViewInit, OnDestroy {
  protected readonly linhasSkeleton = [1, 2, 3, 4, 5, 6];

  @Input({ required: true }) contato!: Contato;
  @Input() mostrarVoltar = false;

  @Output() voltar = new EventEmitter<void>();
  @Output() favoritoAlterado = new EventEmitter<{ contatoId: number; favorito: boolean }>();
  @Output() atendimentoEncerrado = new EventEmitter<number>();
  @Output() atendimentoTransferido = new EventEmitter<AtendimentoTransferido>();

  @ViewChild('areaMensagens') areaMensagens?: ElementRef<HTMLElement>;
  @ViewChild('listaMensagens') listaMensagens?: ElementRef<HTMLElement>;
  @ViewChild(ConversaRodapeComponent) rodape?: ConversaRodapeComponent;
  @ViewChildren(BalaoMensagemComponent) private baloes?: QueryList<BalaoMensagemComponent>;

  mensagens = signal<MensagemModel[]>([]);
  atendimentos = signal<AtendimentoModel[]>([]);
  carregando = signal(true);
  carregandoMais = signal(false);
  sessaoAberta = signal(false);
  aguardandoRespostaTemplate = signal(false);
  dataFechamento = signal<Date | null>(null);
  qtdLembretes = signal(0);
  mostraEncerraAtendimento = signal(false);
  mensagemResposta = signal<MensagemModel | undefined>(undefined);
  figurinhaAberta = signal<MensagemModel | null>(null);
  mostrarBotaoDescer = signal(false);
  selecaoTemplateAberta = signal(false);
  arrastandoArquivo = signal(false);

  readonly existeMensagemCliente = computed(() => this.mensagens().some(m => m.sendBy === 'CLIENTE'));
  readonly itens = computed(() => this.montarItens(this.mensagens(), this.atendimentos()));
  readonly limiteItens = signal(JANELA_INICIAL);
  readonly itensVisiveis = computed(() => {
    const itens = this.itens();
    const limite = this.limiteItens();
    return itens.length > limite ? itens.slice(itens.length - limite) : itens;
  });

  private pagina = 0;
  private temMaisPaginas = true;
  private versaoCarregamento = 0;
  private contadorArraste = 0;
  private socketSubscription?: Subscription;
  private readonly usuarioLogado: UsuarioLogado;
  private readonly idsNovos = new Set<string>();
  private indoParaFim = false;
  private readonly alturas = new WeakMap<Element, number>();
  private readonly reacoesPendentes = new Map<string, ReacaoPendente>();
  private readonly templatesPendentes = new Map<string, EnvioMensagemTemplate>();
  private observadorTamanho?: ResizeObserver;
  private observadorItens?: MutationObserver;
  private removerScroll?: () => void;

  private readonly mensagemService = inject(MensagemService);
  private readonly socketService = inject(SocketService);
  private readonly contatoService = inject(ContatoService);
  private readonly envioTemplateChatService = inject(EnvioTemplateChatService);
  private readonly lembreteService = inject(LembreteService);
  private readonly authService = inject(AuthService);
  private readonly usuarioService = inject(UsuarioService);
  private readonly somTransicaoAudioService = inject(SomTransicaoAudioService);
  private sequenciaAudio = 0;
  private readonly msgService = inject(MsgService);
  private readonly dialog = inject(MatDialog);
  private readonly zone = inject(NgZone);

  constructor() {
    this.usuarioLogado = this.authService.getUsuarioLogadoClariBot() ?? new UsuarioLogado();
    this.socketService.notificacaoListaContato
      .pipe(takeUntilDestroyed())
      .subscribe(payload => this.aoAlterarAtendimento(payload));
  }

  private aoAlterarAtendimento(payload: NovaMensagemContato): void {
    const atualizado = payload?.contato;
    if (!payload?.atendimentoAlterado || !atualizado?.id || atualizado.id !== this.contato?.id) return;
    MethodsUtils.copiarEstadoAtendimento(this.contato, atualizado);
    this.sessaoAberta.set(!!atualizado.temSessaoAberta);
    this.dataFechamento.set(atualizado.dataFechamentoSessao ? new Date(atualizado.dataFechamentoSessao) : null);
    this.atualizarAtendimentos();
    this.verificarPodeEncerrarAtendimento();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contato'] && this.contato && !this.contato.numero) {
      this.contato.numero = this.contato.contato;
    }
    if (changes['contato'] && this.contato?.id && this.contato?.numero) {
      this.iniciarContato();
    }
  }

  ngAfterViewInit(): void {
    const area = this.areaMensagens?.nativeElement;
    const lista = this.listaMensagens?.nativeElement;
    if (!area || !lista) return;
    this.zone.runOutsideAngular(() => {
      const aoRolar = () => this.onScroll();
      area.addEventListener('scroll', aoRolar, { passive: true });
      this.removerScroll = () => area.removeEventListener('scroll', aoRolar);

      this.observadorTamanho = new ResizeObserver(entradas => this.compensarMudancasAbaixo(entradas));
      Array.from(lista.children).forEach(filho => this.observadorTamanho!.observe(filho));
      this.observadorItens = new MutationObserver(mutacoes => {
        for (const mutacao of mutacoes) {
          mutacao.addedNodes.forEach(no => { if (no instanceof Element) this.observadorTamanho?.observe(no); });
          mutacao.removedNodes.forEach(no => { if (no instanceof Element) this.observadorTamanho?.unobserve(no); });
        }
      });
      this.observadorItens.observe(lista, { childList: true });
    });
  }

  ngOnDestroy(): void {
    this.limparReacoesPendentes();
    this.socketSubscription?.unsubscribe();
    this.removerScroll?.();
    this.observadorTamanho?.disconnect();
    this.observadorItens?.disconnect();
  }

  @HostListener('document:keydown.escape', ['$event'])
  aoApertarEsc(event: Event): void {
    if (event.defaultPrevented || this.dialog.openDialogs.length > 0) return;
    if (document.querySelector('.cdk-overlay-pane app-menu-reacao, .cdk-overlay-pane .mat-mdc-select-panel, .cdk-overlay-pane .mat-mdc-menu-panel')) return;
    if (this.rodape?.emojiAberto()) {
      this.rodape.emojiAberto.set(false);
      return;
    }
    if (this.selecaoTemplateAberta()) {
      this.selecaoTemplateAberta.set(false);
      return;
    }
    if (this.mensagemResposta()) {
      this.mensagemResposta.set(undefined);
      return;
    }
    this.voltar.emit();
  }

  get fotoContato(): string {
    return MethodsUtils.fotoPerfilContato(this.contato?.fotoPerfilUrl, this.contato?.statusContato);
  }

  get ehInstagram(): boolean {
    return this.contato?.canalOrigem === CanalOrigemContatoEnum.INSTAGRAM;
  }

  get possuiCanal(): boolean {
    return !!this.contato?.canal?.id;
  }

  get podeEnviar(): boolean {
    return this.sessaoAberta() && this.possuiCanal && this.existeMensagemCliente() && !this.aguardandoRespostaTemplate();
  }

  get motivoBloqueio(): string {
    if (!this.possuiCanal) return 'O contato está em atendimento com o chatbot ou não possui canal atribuído.';
    if (this.aguardandoRespostaTemplate()) return 'Aguarde o cliente responder ao template.';
    if (!this.existeMensagemCliente()) return 'Aguarde o cliente responder.';
    return '';
  }

  private iniciarContato(): void {
    const versao = ++this.versaoCarregamento;
    this.pagina = 0;
    this.temMaisPaginas = true;
    this.mensagens.set([]);
    this.atendimentos.set([]);
    this.mensagemResposta.set(undefined);
    this.selecaoTemplateAberta.set(false);
    this.mostrarBotaoDescer.set(false);
    this.limiteItens.set(JANELA_INICIAL);
    this.idsNovos.clear();
    this.limparReacoesPendentes();
    this.templatesPendentes.clear();
    this.indoParaFim = false;
    if (this.areaMensagens) this.areaMensagens.nativeElement.scrollTop = 0;
    this.sessaoAberta.set(!!this.contato.temSessaoAberta);
    this.aguardandoRespostaTemplate.set(false);
    this.dataFechamento.set(this.contato.dataFechamentoSessao ? new Date(this.contato.dataFechamentoSessao) : null);
    this.qtdLembretes.set(0);
    this.carregando.set(true);

    this.escutarSocket(this.contato.numero);
    this.buscarPagina(versao, true);
    this.buscarLembretes();
    this.verificarPodeEncerrarAtendimento();
  }

  private buscarPagina(versao: number, inicial: boolean, aoConcluir?: () => void): void {
    const filtro = new Contato();
    filtro.id = this.contato.id;
    filtro.numero = this.contato.numero;

    this.mensagemService.buscarMensagensContatoSelecionado(filtro, true, this.pagina).subscribe({
      next: (res: any) => {
        if (versao !== this.versaoCarregamento) return;
        const novas: MensagemModel[] = res?.mensagens ?? [];
        const novosAtendimentos: AtendimentoModel[] = res?.atendimentos ?? [];
        this.temMaisPaginas = res?.temMaisPaginas ?? false;

        if (inicial) {
          this.mensagens.set(this.mesclarMensagens(novas, this.mensagens()));
          this.atendimentos.set(novosAtendimentos);
        } else {
          const antes = this.itens().length;
          this.mensagens.set(this.mesclarMensagens(novas, this.mensagens()));
          this.atendimentos.set([...novosAtendimentos, ...this.atendimentos()]);
          const acrescentados = this.itens().length - antes;
          if (acrescentados > 0) this.limiteItens.update(limite => limite + acrescentados);
        }
        this.atualizarEstadoSessao();
      },
      error: () => {
        if (versao !== this.versaoCarregamento) return;
        this.msgService.msgErro('Erro ao buscar as mensagens do contato.');
        if (!inicial) this.pagina--;
        this.carregando.set(false);
        this.carregandoMais.set(false);
        aoConcluir?.();
      },
      complete: () => {
        if (versao !== this.versaoCarregamento) return;
        this.carregando.set(false);
        this.carregandoMais.set(false);
        if (aoConcluir) {
          setTimeout(aoConcluir, 0);
          return;
        }
        setTimeout(() => this.verificarPreCarga(), 0);
      }
    });
  }

  private atualizarAtendimentos(): void {
    const versao = this.versaoCarregamento;
    const filtro = new Contato();
    filtro.id = this.contato.id;
    filtro.numero = this.contato.numero;

    this.mensagemService.buscarMensagensContatoSelecionado(filtro, true, 0).subscribe({
      next: (res: any) => {
        if (versao !== this.versaoCarregamento) return;
        const recentes: AtendimentoModel[] = res?.atendimentos ?? [];
        const idsRecentes = new Set(recentes.map(at => at.id));
        this.atendimentos.set([...this.atendimentos().filter(at => !idsRecentes.has(at.id)), ...recentes]);
      }
    });
  }

  private buscarLembretes(): void {
    const contatoId = this.contato.id;
    this.lembreteService.contarPendentesContato(contatoId).subscribe({
      next: qtd => {
        if (contatoId === this.contato.id) this.qtdLembretes.set(qtd ?? 0);
      },
      error: () => this.qtdLembretes.set(0)
    });
  }

  private verificarPodeEncerrarAtendimento(): void {
    const contatoId = this.contato.id;
    this.mostraEncerraAtendimento.set(!!this.usuarioLogado.id && this.contato.operador?.id === this.usuarioLogado.id);
    if (this.usuarioLogado.isCoordenador && !this.mostraEncerraAtendimento()) {
      this.contatoService.verificarUsuarioIsCoordenadorCanalContatoId(contatoId).subscribe(res => {
        if (res && contatoId === this.contato.id) this.mostraEncerraAtendimento.set(true);
      });
    }
  }

  private onScroll(): void {
    const el = this.areaMensagens?.nativeElement;
    if (!el) return;
    const distanciaDoFim = Math.abs(el.scrollTop);
    if (distanciaDoFim <= TOLERANCIA_FIM_PX) this.indoParaFim = false;

    const mostrarDescer = distanciaDoFim > 600;
    if (mostrarDescer !== this.mostrarBotaoDescer()) this.zone.run(() => this.mostrarBotaoDescer.set(mostrarDescer));

    if (distanciaDoFim < 80 && this.limiteItens() > LIMITE_ENCOLHER_JANELA && !this.carregandoMais()) {
      this.zone.run(() => this.limiteItens.set(JANELA_INICIAL));
      return;
    }
    this.verificarPreCarga();
  }

  private compensarMudancasAbaixo(entradas: ResizeObserverEntry[]): void {
    const area = this.areaMensagens?.nativeElement;
    if (!area) return;
    const noFim = Math.abs(area.scrollTop) <= TOLERANCIA_FIM_PX;
    const baseArea = area.getBoundingClientRect().bottom;
    let deslocamento = 0;

    for (const entrada of entradas) {
      const alvo = entrada.target;
      const altura = entrada.borderBoxSize?.[0]?.blockSize ?? alvo.getBoundingClientRect().height;
      const anterior = this.alturas.get(alvo) ?? 0;
      this.alturas.set(alvo, altura);
      const delta = altura - anterior;
      if (!delta || noFim || this.indoParaFim || !alvo.isConnected) continue;
      const topoAntigo = alvo.getBoundingClientRect().top + delta;
      if (topoAntigo >= baseArea - 1) deslocamento += delta;
    }

    if (deslocamento) area.scrollTop -= deslocamento;
  }

  private verificarPreCarga(): void {
    const el = this.areaMensagens?.nativeElement;
    if (!el || this.carregando() || this.carregandoMais()) return;
    const distanciaDoTopo = el.scrollHeight - el.clientHeight - Math.abs(el.scrollTop);
    if (distanciaDoTopo >= el.clientHeight * TELAS_PRE_CARGA) return;
    if (this.itens().length > this.limiteItens()) {
      this.zone.run(() => this.limiteItens.update(limite => limite + PASSO_JANELA));
      return;
    }
    if (!this.temMaisPaginas) return;
    this.zone.run(() => {
      this.carregandoMais.set(true);
      this.pagina++;
      this.buscarPagina(this.versaoCarregamento, false);
    });
  }

  private carregarPaginaAnterior(): Promise<boolean> {
    return new Promise(resolve => {
      if (!this.temMaisPaginas) {
        resolve(false);
        return;
      }
      if (this.carregando() || this.carregandoMais()) {
        setTimeout(() => this.carregarPaginaAnterior().then(resolve), 150);
        return;
      }
      this.carregandoMais.set(true);
      this.pagina++;
      this.buscarPagina(this.versaoCarregamento, false, () => resolve(true));
    });
  }

  async irParaMensagem(alvo: { id?: string; metaId?: string }): Promise<void> {
    const versao = this.versaoCarregamento;
    for (let tentativa = 0; tentativa <= MAX_PAGINAS_BUSCA_MENSAGEM; tentativa++) {
      if (this.revelarNaJanela(alvo)) await new Promise(resolve => setTimeout(resolve));
      const elemento = this.encontrarBalao(alvo);
      if (elemento) {
        this.rolarEDestacar(elemento);
        return;
      }
      const carregou = await this.carregarPaginaAnterior();
      if (!carregou || versao !== this.versaoCarregamento) break;
    }
    if (versao === this.versaoCarregamento) this.msgService.msgAdvertencia('Mensagem original não encontrada na conversa.');
  }

  async localizarNoChat(alvo: AlvoLocalizacaoChat): Promise<void> {
    const versao = this.versaoCarregamento;
    for (let tentativa = 0; tentativa <= MAX_PAGINAS_BUSCA_MENSAGEM; tentativa++) {
      const chave = this.chaveDoAlvo(alvo, !this.temMaisPaginas);
      if (chave) {
        await this.revelarChaveERolar(chave);
        return;
      }
      const carregou = await this.carregarPaginaAnterior();
      if (!carregou || versao !== this.versaoCarregamento) break;
    }
    if (versao !== this.versaoCarregamento) return;
    const chave = this.chaveDoAlvo(alvo, true);
    if (chave) {
      await this.revelarChaveERolar(chave);
      return;
    }
    this.msgService.msgAdvertencia('Não foi possível localizar este momento na conversa.');
  }

  private chaveDoAlvo(alvo: AlvoLocalizacaoChat, inicioCarregado: boolean): string | null {
    const itens = this.itens().filter(item => item.tipo !== 'data');
    if (alvo.atendimentoId != null) {
      const chaveEvento = `${alvo.abertura === false ? 'at-fim' : 'at-ini'}-${alvo.atendimentoId}`;
      if (itens.some(item => item.chave === chaveEvento)) return chaveEvento;
    }
    if (!alvo.data || !itens.length) return null;
    const alvoMs = new Date(alvo.data).getTime();
    if (!inicioCarregado && (itens[0].data ?? Infinity) > alvoMs) return null;
    return (itens.find(item => (item.data ?? 0) >= alvoMs) ?? itens[itens.length - 1]).chave;
  }

  private async revelarChaveERolar(chave: string): Promise<void> {
    const itens = this.itens();
    const necessario = itens.length - itens.findIndex(item => item.chave === chave) + 10;
    if (necessario > this.limiteItens()) {
      this.limiteItens.set(necessario);
      await new Promise(resolve => setTimeout(resolve));
    }
    const elemento = this.areaMensagens?.nativeElement.querySelector<HTMLElement>(`[data-chave="${CSS.escape(chave)}"]`);
    if (elemento) this.rolarEDestacar(elemento);
  }

  private rolarEDestacar(elemento: HTMLElement): void {
    elemento.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setTimeout(() => elemento.scrollIntoView({ block: 'center', behavior: 'smooth' }), 450);
    elemento.classList.remove(...CLASSES_DESTAQUE_CONVERSA);
    void elemento.offsetWidth;
    elemento.classList.add(...CLASSES_DESTAQUE_CONVERSA);
    setTimeout(() => elemento.classList.remove(...CLASSES_DESTAQUE_CONVERSA), DURACAO_DESTAQUE_CONVERSA_MS);
  }

  private revelarNaJanela(alvo: { id?: string; metaId?: string }): boolean {
    const itens = this.itens();
    const indice = itens.findIndex(item => !!item.mensagem
      && ((!!alvo.metaId && item.mensagem.metaId === alvo.metaId) || (!!alvo.id && item.mensagem.id === alvo.id)));
    if (indice < 0) return false;
    const necessario = itens.length - indice + 10;
    if (necessario <= this.limiteItens()) return false;
    this.limiteItens.set(necessario);
    return true;
  }

  private encontrarBalao(alvo: { id?: string; metaId?: string }): HTMLElement | null {
    const area = this.areaMensagens?.nativeElement;
    if (!area) return null;
    const seletores = [
      alvo.metaId ? `[data-meta-id="${CSS.escape(alvo.metaId)}"]` : '',
      alvo.id ? `[data-msg-id="${CSS.escape(alvo.id)}"]` : ''
    ].filter(Boolean);
    for (const seletor of seletores) {
      const linha = area.querySelector<HTMLElement>(seletor);
      if (linha) return linha.closest<HTMLElement>('app-balao-mensagem, app-album-imagens');
    }
    return null;
  }

  descerAoFinal(): void {
    const area = this.areaMensagens?.nativeElement;
    if (!area) return;
    this.indoParaFim = Math.abs(area.scrollTop) > TOLERANCIA_FIM_PX;
    area.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private escutarSocket(numero: string): void {
    this.socketSubscription?.unsubscribe();
    this.socketSubscription = this.socketService.escutarAtualizacaoChatPorContato(numero)
      .subscribe(novas => {
        if (numero !== this.contato?.numero) return;
        this.aplicarMensagensSocket(novas ?? []);
      });
  }

  private aplicarMensagensSocket(novas: MensagemModel[]): void {
    const ids = new Set(novas.map(n => n.id));
    const atuais = this.mensagens().filter(m => !(m.isMensagemTemp && ids.has(m.id)));
    const indicePorId = new Map(atuais.map((m, indice) => [m.id, indice]));

    for (const nova of novas) {
      if (!nova.id) continue;
      const indice = indicePorId.get(nova.id);
      if (indice !== undefined) {
        const alteracoes: Partial<MensagemModel> = {};
        const reacaoPendente = !!nova.metaId && this.reacoesPendentes.has(nova.metaId);
        (Object.keys(nova) as (keyof MensagemModel)[]).forEach(chave => {
          const valor = nova[chave];
          if (String(chave).startsWith('reacao')) {
            if (!reacaoPendente) (alteracoes as any)[chave] = valor ?? undefined;
            return;
          }
          if (valor !== null && valor !== undefined) (alteracoes as any)[chave] = valor;
        });
        atuais[indice] = this.copiarMensagem(atuais[indice], { ...alteracoes, formatada: false });
      } else {
        indicePorId.set(nova.id, atuais.length);
        atuais.push(nova);
        this.idsNovos.add(nova.id);
      }
    }

    if (novas.some(m => (m.sendBy === 'CLIENTE' && !m.isTemplate) || m.isTemplate)) {
      this.sessaoAberta.set(true);
      this.contato.temSessaoAberta = true;
    }
    const ultimaDoCliente = novas.filter(m => m.sendBy === 'CLIENTE' && m.date)
      .map(m => new Date(m.date).getTime()).sort((a, b) => b - a)[0];
    if (ultimaDoCliente) {
      this.dataFechamento.set(new Date(ultimaDoCliente + JANELA_SESSAO_MS));
    }

    this.mensagens.set(atuais);
    this.atualizarEstadoSessao();
  }

  private atualizarEstadoSessao(): void {
    let ultima: MensagemModel | undefined;
    let dataUltima = -Infinity;
    for (const m of this.mensagens()) {
      if (!m.date || m.isMensagemTemp) continue;
      const data = new Date(m.date).getTime();
      if (data >= dataUltima) {
        dataUltima = data;
        ultima = m;
      }
    }
    this.aguardandoRespostaTemplate.set(!!ultima?.isTemplate);
  }

  private copiarMensagem(mensagem: MensagemModel, alteracoes: Partial<MensagemModel>): MensagemModel {
    return Object.assign(Object.create(Object.getPrototypeOf(mensagem)), mensagem, alteracoes);
  }

  private alterarMensagem(encontrar: (m: MensagemModel) => boolean, alteracoes: Partial<MensagemModel>): void {
    const lista = this.mensagens();
    const indice = lista.findIndex(encontrar);
    if (indice < 0) return;
    const copia = [...lista];
    copia[indice] = this.copiarMensagem(lista[indice], alteracoes);
    this.mensagens.set(copia);
  }

  private mesclarMensagens(anteriores: MensagemModel[], atuais: MensagemModel[]): MensagemModel[] {
    const ids = new Set(atuais.map(m => m.id));
    return [...anteriores.filter(m => !m.id || !ids.has(m.id)), ...atuais];
  }

  abrirDetalhesAtendimento(atendimentoId?: number): void {
    if (atendimentoId == null) return;
    this.dialog.open(ModalAtendimentoComponent, MethodsUtils.configPainelLateralChat<ModalAtendimentoData>({ atendimentoId }, '420px'));
  }

  private montarItens(mensagens: MensagemModel[], atendimentos: AtendimentoModel[]): ItemConversa[] {
    const eventos: { data: number; item: ItemConversa }[] = [];
    const vistos = new Set<number>();

    for (const at of atendimentos) {
      if (at.id != null) {
        if (vistos.has(at.id)) continue;
        vistos.add(at.id);
      }
      const operador = at.operador?.nome ?? '';
      if (at.dataCriacao) {
        eventos.push({
          data: new Date(at.dataCriacao).getTime(),
          item: {
            tipo: 'atendimento', chave: `at-ini-${at.id}`, abertura: true, atendimentoId: at.id, label: 'Atendimento iniciado',
            dica: `ID ${at.id ?? ''}${operador ? ' · ' + operador : ''} · ${this.formatarDataHora(at.dataCriacao)}`
          }
        });
      }
      if (at.dataFim) {
        eventos.push({
          data: new Date(at.dataFim).getTime(),
          item: {
            tipo: 'atendimento', chave: `at-fim-${at.id}`, abertura: false, atendimentoId: at.id, label: 'Atendimento encerrado',
            dica: `ID ${at.id ?? ''}${operador ? ' · ' + operador : ''} · ${this.formatarDataHora(at.dataFim)}`
          }
        });
      }
    }

    const todos = [
      ...mensagens.map((m, i) => ({
        data: new Date(m.date ?? 0).getTime(),
        item: { tipo: 'mensagem', chave: `msg-${m.id || m.metaId || i}`, mensagem: m, nova: !!m.id && this.idsNovos.has(m.id) } as ItemConversa
      })),
      ...eventos
    ].sort((a, b) => a.data - b.data);

    const itens: ItemConversa[] = [];
    let diaAtual = '';
    let ultimaData = 0;
    for (const { data, item } of todos) {
      const dia = new Date(data).toDateString();
      if (dia !== diaAtual) {
        diaAtual = dia;
        itens.push({ tipo: 'data', chave: `dia-${dia}`, label: MethodsUtils.rotuloDia(new Date(data)) });
      }
      const anterior = itens[itens.length - 1];
      item.data = data;
      if (this.entraNoAlbum(item, anterior, data - ultimaData)) {
        const primeira = anterior.tipo === 'album' ? anterior.mensagens![0] : anterior.mensagem!;
        const mensagensAlbum = anterior.tipo === 'album' ? [...anterior.mensagens!, item.mensagem!] : [anterior.mensagem!, item.mensagem!];
        itens[itens.length - 1] = {
          tipo: 'album', chave: `album-${primeira.id || primeira.metaId}`, mensagens: mensagensAlbum,
          nova: !!anterior.nova || !!item.nova, data: anterior.data
        };
      } else {
        itens.push(item);
      }
      ultimaData = data;
    }
    return itens;
  }

  private entraNoAlbum(item: ItemConversa, anterior: ItemConversa | undefined, intervalo: number): boolean {
    if (!anterior || item.tipo !== 'mensagem' || !this.ehImagemDeAlbum(item.mensagem!) || intervalo > INTERVALO_ALBUM_MS) return false;
    const base = anterior.tipo === 'album' ? anterior.mensagens![0] : anterior.tipo === 'mensagem' ? anterior.mensagem! : null;
    if (!base || (anterior.tipo === 'mensagem' && !this.ehImagemDeAlbum(base))) return false;
    return base.sendBy === item.mensagem!.sendBy;
  }

  private ehImagemDeAlbum(m: MensagemModel): boolean {
    return !!m.urlImage && !m.mensagem?.trim() && !MethodsUtils.ehFigurinha(m) && !m.mensagemResposta && !m.reacao
      && this.tipoGaleria(m) === 'imagem' && !(m.isMensagemTemp && m.status === MensagemStatusEnum.FAILED);
  }

  private tipoGaleria(m: MensagemModel): TipoItemGaleria | null {
    const extensao = MethodsUtils.extensaoDoArquivo(m.extensao || m.urlImage || '');
    return TIPO_GALERIA_POR_EXTENSAO[extensao] ?? null;
  }

  private formatarDataHora(data: Date | string): string {
    const d = new Date(data);
    return `${d.toLocaleDateString('pt-BR')} às ${this.formatarHora(d)}`;
  }

  private formatarHora(data: Date | string): string {
    return new Date(data).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }

  get horaAgora(): string {
    return this.formatarHora(new Date());
  }

  enviarTexto(texto: string): void {
    const temp = this.criarMensagemTemp(texto, 'text');
    this.adicionarTemp(temp);
    this.mensagemService.enviarMensagem(this.montarMensagemEnvio(temp.id, texto, temp.mensagemResposta), undefined, true).subscribe({
      next: () => this.confirmarEnvio(temp.id),
      error: () => this.marcarFalha(temp.id, 'Não foi possível enviar a mensagem.')
    });
    this.mensagemResposta.set(undefined);
  }

  enviarArquivos(envio: EnvioArquivosChat): void {
    envio.arquivos.forEach((arquivo, indice) => {
      if (!arquivo.name.includes('.')) {
        this.msgService.msgErro('Arquivo sem extensão válida!');
        return;
      }
      const extensao = MethodsUtils.extensaoDoArquivo(arquivo.name);
      const limite = LIMITES_ARQUIVO_MB[extensao];
      if (limite && arquivo.size / (1024 * 1024) > limite) {
        this.msgService.msgAdvertencia(`O arquivo excede o limite de ${limite} MB para arquivos .${extensao}`);
        return;
      }

      const texto = indice === 0 ? envio.texto : '';
      const tipo = EXT_POR_TIPO.find(e => e.exts.includes(extensao))?.tipo ?? 'document';
      const temp = this.criarMensagemTemp(texto, tipo);
      temp.uuidImage = 'msgUuidFake';
      temp.urlImage = URL.createObjectURL(arquivo);
      temp.extensao = extensao;
      temp.arquivoLocal = arquivo;
      this.adicionarTemp(temp);
      this.enviarArquivoTemp(temp, arquivo, texto);
    });
    this.mensagemResposta.set(undefined);
  }

  private enviarArquivoTemp(temp: MensagemModel, arquivo: File, texto: string): void {
    this.mensagemService.enviarMensagem(this.montarMensagemEnvio(temp.id, texto, temp.mensagemResposta), arquivo, true).subscribe({
      next: () => this.confirmarEnvio(temp.id),
      error: () => this.marcarFalha(temp.id, 'Não foi possível enviar o arquivo.')
    });
  }

  reenviarMensagem(mensagem: MensagemModel): void {
    if (mensagem.isMensagemTemp && this.templatesPendentes.has(mensagem.id)) {
      this.alterarMensagem(m => m.id === mensagem.id && !!m.isMensagemTemp,
        { status: MensagemStatusEnum.SENT, enviando: true, erroMensagem: undefined });
      this.enviarTemplateTemp(mensagem.id);
      return;
    }
    if (mensagem.isMensagemTemp) {
      this.alterarMensagem(m => m.id === mensagem.id && !!m.isMensagemTemp,
        { status: MensagemStatusEnum.SENT, enviando: true, erroMensagem: undefined });
      if (mensagem.arquivoLocal) {
        this.enviarArquivoTemp(mensagem, mensagem.arquivoLocal, mensagem.mensagem);
      } else {
        this.mensagemService.enviarMensagem(this.montarMensagemEnvio(mensagem.id, mensagem.mensagem, mensagem.mensagemResposta), undefined, true).subscribe({
          next: () => this.confirmarEnvio(mensagem.id),
          error: () => this.marcarFalha(mensagem.id, 'Não foi possível enviar a mensagem.')
        });
      }
      return;
    }

    const infos = new TelefoneIdDataMensagem();
    infos.mensagemId = mensagem.id;
    infos.contato = this.contato.numero;
    infos.data = mensagem.date;
    this.alterarMensagem(m => m.id === mensagem.id, { enviando: true });
    this.mensagemService.reenviarMensagem(infos, true).subscribe({
      next: () => this.alterarMensagem(m => m.id === mensagem.id,
        { enviando: false, status: MensagemStatusEnum.SENT, erroMensagem: undefined }),
      error: erro => {
        const motivo = erro?.error?.message ?? 'Não foi possível enviar a mensagem.';
        this.alterarMensagem(m => m.id === mensagem.id,
          { enviando: false, status: MensagemStatusEnum.FAILED, erroMensagem: motivo });
        if (erro?.status === 400) this.msgService.msgErro(motivo);
      }
    });
  }

  reagirMensagem({ mensagem, emoji }: ReacaoBalao): void {
    const chave = mensagem.metaId;
    if (!chave) return;
    const pendente = this.reacoesPendentes.get(chave);
    this.aplicarReacaoLocal(chave, emoji);

    const entrada: ReacaoPendente = pendente ?? { emoji, confirmado: mensagem.reacao ?? '', timer: null, enviando: false };
    entrada.emoji = emoji;
    if (entrada.timer) clearTimeout(entrada.timer);
    entrada.timer = setTimeout(() => this.enviarReacao(chave, mensagem.date), ESPERA_REACAO_MS);
    this.reacoesPendentes.set(chave, entrada);
  }

  private enviarReacao(chave: string, dataMensagem: Date): void {
    const entrada = this.reacoesPendentes.get(chave);
    if (!entrada) return;
    if (entrada.enviando) {
      entrada.timer = setTimeout(() => this.enviarReacao(chave, dataMensagem), 250);
      return;
    }
    entrada.timer = null;
    if (entrada.emoji === entrada.confirmado) {
      this.reacoesPendentes.delete(chave);
      return;
    }
    const emoji = entrada.emoji;
    entrada.enviando = true;
    this.mensagemService.reagirMensagem({ contato: this.contato.numero, metaId: chave, emoji, dataMensagem }).subscribe({
      next: () => {
        entrada.enviando = false;
        entrada.confirmado = emoji;
        if (!entrada.timer) this.reacoesPendentes.delete(chave);
      },
      error: (erro) => {
        entrada.enviando = false;
        this.reacoesPendentes.delete(chave);
        this.aplicarReacaoLocal(chave, entrada.confirmado);
        this.msgService.msgErro(erro?.error?.mensagem || erro?.error?.message || 'Não foi possível enviar a reação.');
      }
    });
  }

  private aplicarReacaoLocal(chave: string, emoji: string): void {
    const autor = emoji
      ? { reacaoUsuarioId: this.usuarioLogado.id, reacaoUsuarioNome: this.usuarioLogado.nome,
          reacaoUsuarioUuid: (this.usuarioLogado as any).uuid, reacaoUsuarioExtensaoImg: this.usuarioLogado.extensaoImg }
      : { reacaoUsuarioId: undefined, reacaoUsuarioNome: undefined, reacaoUsuarioUuid: undefined, reacaoUsuarioExtensaoImg: undefined };
    this.alterarMensagem(m => m.metaId === chave, { reacao: emoji, ...autor });
  }

  private limparReacoesPendentes(): void {
    this.reacoesPendentes.forEach(entrada => entrada.timer && clearTimeout(entrada.timer));
    this.reacoesPendentes.clear();
  }

  aoEnviarTemplate(envio: EnvioMensagemTemplate): void {
    this.selecaoTemplateAberta.set(false);
    const id = uuidv4();
    envio.id = id;
    this.templatesPendentes.set(id, envio);
    this.adicionarTemp(this.criarTemplateTemp(id, envio));
    this.enviarTemplateTemp(id);
  }

  private enviarTemplateTemp(id: string): void {
    const envio = this.templatesPendentes.get(id);
    if (!envio) return;
    const versao = this.versaoCarregamento;
    this.marcarSessaoTemplate(true);
    this.envioTemplateChatService.confirmar(envio).then(enviado => {
      if (versao !== this.versaoCarregamento) return;
      if (enviado) {
        this.templatesPendentes.delete(id);
        this.confirmarEnvio(id);
        return;
      }
      this.marcarSessaoTemplate(false);
      this.marcarFalha(id, 'Não foi possível enviar o template.');
    });
  }

  private marcarSessaoTemplate(aberta: boolean): void {
    this.sessaoAberta.set(aberta);
    this.aguardandoRespostaTemplate.set(aberta);
    this.contato.temSessaoAberta = aberta;
  }

  private criarTemplateTemp(id: string, envio: EnvioMensagemTemplate): MensagemModel {
    const msg = this.criarMensagemTemp(this.substituirVariaveis(envio.textoCorpo, envio), '');
    msg.id = id;
    msg.isTemplate = true;
    msg.mensagemResposta = undefined;
    msg.mensagemCabecalho = this.substituirVariaveis(envio.textoCabecalho, envio);
    msg.mensagemRodape = this.substituirVariaveis(envio.textoRodape, envio);
    msg.urlImage = envio.urlImagem;
    msg.botoes = envio.botoes.map(b => ({ titulo: b.titulo, valor: b.valor }));
    msg.nomeUsuarioEnvio = this.usuarioLogado.nome ?? '';
    return msg;
  }

  private substituirVariaveis(texto: string, envio: EnvioMensagemTemplate): string {
    return envio.variaveis.reduce((resultado, v) => resultado.split('{{' + v.ordem + '}}').join(v.valor), texto ?? '');
  }

  private adicionarTemp(temp: MensagemModel): void {
    this.idsNovos.add(temp.id);
    this.mensagens.set([...this.mensagens(), temp]);
    this.descerAoFinal();
  }

  private confirmarEnvio(id: string): void {
    this.alterarMensagem(m => m.id === id && !!m.isMensagemTemp, { enviando: false });
  }

  private marcarFalha(id: string, erro: string): void {
    this.alterarMensagem(m => m.id === id && !!m.isMensagemTemp,
      { enviando: false, status: MensagemStatusEnum.FAILED, erroMensagem: erro });
  }

  private criarMensagemTemp(texto: string, tipo: string): MensagemModel {
    const msg = new MensagemModel(texto);
    msg.id = uuidv4();
    msg.date = new Date();
    msg.status = MensagemStatusEnum.SENT;
    msg.type = tipo;
    msg.isTemplate = false;
    msg.isMensagemTemp = true;
    msg.enviando = true;
    msg.sendBy = MensagemTipoEnum.SISTEMA;
    msg.mensagemResposta = this.mensagemResposta();
    return msg;
  }

  private montarMensagemEnvio(id: string, texto: string, resposta?: MensagemModel): MensagemModel {
    const msg = new MensagemModel(texto);
    msg.id = id;
    msg.nome = this.contato.nome ?? '';
    msg.contato = this.contato.numero;
    msg.nomeUsuarioEnvio = this.usuarioLogado.nome ?? '';
    msg.mensagemResposta = resposta;
    return msg;
  }

  aoArrastarEntrar(event: DragEvent): void {
    if (!this.temArquivos(event)) return;
    event.preventDefault();
    this.contadorArraste++;
    this.arrastandoArquivo.set(true);
  }

  aoArrastarSobre(event: DragEvent): void {
    if (this.temArquivos(event)) event.preventDefault();
  }

  aoArrastarSair(): void {
    this.contadorArraste = Math.max(0, this.contadorArraste - 1);
    if (this.contadorArraste === 0) this.arrastandoArquivo.set(false);
  }

  aoSoltar(event: DragEvent): void {
    if (!this.temArquivos(event)) return;
    event.preventDefault();
    this.contadorArraste = 0;
    this.arrastandoArquivo.set(false);
    this.rodape?.adicionarArquivos(Array.from(event.dataTransfer?.files ?? []));
  }

  private temArquivos(event: DragEvent): boolean {
    return Array.from(event.dataTransfer?.types ?? []).includes('Files');
  }

  alternarFavorito(): void {
    const contato = this.contato;
    const novoValor = !contato.favorito;
    contato.favorito = novoValor;
    this.favoritoAlterado.emit({ contatoId: contato.id, favorito: novoValor });

    this.contatoService.alterarFavorito(contato.id, novoValor).subscribe({
      error: () => {
        contato.favorito = !novoValor;
        this.favoritoAlterado.emit({ contatoId: contato.id, favorito: !novoValor });
        this.msgService.msgErro('Não foi possível atualizar o contato como importante.');
      }
    });
  }

  abrirLembretes(): void {
    this.abrirPerfil('detalhes', true);
  }

  editarContato(): void {
    this.dialog.open(ModalEditarContatoComponent, {
      data: { contatoId: this.contato.id, nome: this.contato.nome },
      panelClass: 'border-radius-7'
    }).afterClosed().subscribe((nome?: string) => {
      if (nome) this.contato.nome = nome;
    });
  }

  async tocarProximoAudio(mensagem: MensagemModel): Promise<void> {
    const ordenadas = [...this.mensagens()].sort((a, b) => new Date(a.date ?? 0).getTime() - new Date(b.date ?? 0).getTime());
    const proxima = ordenadas[ordenadas.indexOf(mensagem) + 1];
    if (!proxima || MethodsUtils.tipoPreviaMensagem(proxima) !== TipoPreviaMensagemEnum.AUDIO) return;
    const sequencia = ++this.sequenciaAudio;
    await this.somTransicaoAudioService.tocar();
    const algumTocando = Array.from(document.querySelectorAll('audio')).some(audio => !audio.paused);
    if (sequencia !== this.sequenciaAudio || algumTocando) return;
    this.baloes?.find(balao => balao.mensagem === proxima)?.tocarAudio();
  }

  private abrirFigurinha(mensagem: MensagemModel): void {
    const doCliente = mensagem.sendBy === MensagemTipoEnum.CLIENTE;
    const minha = !doCliente && mensagem.idUsuarioEnvio != null && Number(mensagem.idUsuarioEnvio) === Number(this.usuarioLogado.id);
    const autor$ = minha ? this.usuarioService.buscarUsuarioLogadoEmCache() : of(null);
    autor$.pipe(take(1)).subscribe(usuario => {
      const nomeContato = this.contato.nome || this.contato.numero;
      const data: VisualizadorFigurinhaData = {
        url: mensagem.urlImage,
        data: mensagem.date,
        autor: doCliente ? nomeContato : minha ? 'Você' : (mensagem.nomeUsuarioEnvio || 'Você'),
        fotoAutor: doCliente ? this.fotoContato : undefined,
        fotoAutorReal: doCliente && !!this.contato.fotoPerfilUrl,
        pessoaAutor: doCliente
          ? { nome: nomeContato }
          : { id: usuario?.id, nome: usuario?.nome ?? mensagem.nomeUsuarioEnvio, uuid: usuario?.uuid, extensaoImg: usuario?.extensaoImg },
        podeResponder: this.sessaoAberta() && !!mensagem.metaId
      };
      this.figurinhaAberta.set(mensagem);
      this.dialog.open<VisualizadorFigurinhaComponent, VisualizadorFigurinhaData, VisualizadorFigurinhaResultado>(VisualizadorFigurinhaComponent, {
        data,
        width: '100vw',
        height: '100dvh',
        maxWidth: '100vw',
        maxHeight: '100dvh',
        panelClass: ['galeria-midia-painel', CLASSE_FONTE_CHAT],
        backdropClass: 'galeria-midia-fundo',
        autoFocus: false
      }).afterClosed().subscribe(resultado => {
        this.figurinhaAberta.set(null);
        if (resultado?.responder) this.mensagemResposta.set(mensagem);
      });
    });
  }

  private itensGaleria(): { imagens: MensagemModel[]; itens: ItemGaleria[] } {
    const imagens = this.mensagens()
      .filter(m => !!m.urlImage && !MethodsUtils.ehFigurinha(m) && !!this.tipoGaleria(m))
      .sort((a, b) => new Date(a.date ?? 0).getTime() - new Date(b.date ?? 0).getTime());
    const itens: ItemGaleria[] = imagens.map(m => ({
      url: m.urlImage,
      tipo: this.tipoGaleria(m) ?? 'imagem',
      data: m.date,
      mensagemId: m.id,
      metaId: m.metaId,
      autor: m.sendBy === 'CLIENTE' ? (this.contato.nome || this.contato.numero) : (m.nomeUsuarioEnvio || 'Você')
    }));
    return { imagens, itens };
  }

  abrirGaleria(mensagem: MensagemModel): void {
    if (MethodsUtils.ehFigurinha(mensagem)) {
      this.abrirFigurinha(mensagem);
      return;
    }
    const { imagens, itens } = this.itensGaleria();
    const indice = Math.max(0, imagens.findIndex(m => m === mensagem || (!!m.id && m.id === mensagem.id)));
    if (!itens.length) return;

    const data: GaleriaMidiaData = {
      itens,
      indiceInicial: indice,
      temMaisAnteriores: this.temMaisPaginas,
      carregarAnteriores: async () => {
        const antes = this.itensGaleria().itens.length;
        let carregou = true;
        while (carregou && this.itensGaleria().itens.length === antes) {
          carregou = await this.carregarPaginaAnterior();
        }
        return { itens: this.itensGaleria().itens, temMais: this.temMaisPaginas };
      }
    };

    this.dialog.open<GaleriaMidiaComponent, GaleriaMidiaData, GaleriaMidiaResultado>(GaleriaMidiaComponent, {
      data,
      width: '100vw',
      height: '100dvh',
      maxWidth: '100vw',
      maxHeight: '100dvh',
      panelClass: ['galeria-midia-painel', CLASSE_FONTE_CHAT],
      backdropClass: 'galeria-midia-fundo',
      autoFocus: false
    }).afterClosed().subscribe(resultado => {
      if (resultado?.abrirNoChat) this.irParaMensagem(resultado.abrirNoChat);
    });
  }

  abrirPerfil(visao: 'detalhes' | 'transferir' = 'detalhes', focarLembrete = false): void {
    const contato = this.contato;
    const favoritoAntes = !!contato.favorito;
    const data: PerfilContatoChatData = {
      contato,
      mostraEncerraAtendimento: this.mostraEncerraAtendimento(),
      visaoInicial: visao,
      focarLembrete
    };

    this.dialog.open(PerfilContatoChatComponent, MethodsUtils.configPainelLateralChat(data)).afterClosed().subscribe((resultado?: PerfilContatoChatResultado) => {
      if (!!contato.favorito !== favoritoAntes) {
        this.favoritoAlterado.emit({ contatoId: contato.id, favorito: !!contato.favorito });
      }
      if (resultado?.lembreteCriado) this.buscarLembretes();
      if (resultado?.localizarNoChat) this.localizarNoChat(resultado.localizarNoChat);
      if (resultado?.encerrouSessao) {
        contato.temSessaoAberta = false;
        this.sessaoAberta.set(false);
      }
      if (resultado?.encerrouAtendimento || resultado?.encerrouSessao) {
        this.mostraEncerraAtendimento.set(false);
        this.atualizarAtendimentos();
        this.atendimentoEncerrado.emit(contato.id);
      }
      if (resultado?.transferiu) {
        contato.canal = resultado.canalTransferencia as CanalModel;
        contato.operador = resultado.operadorTransferencia;
        this.atualizarAtendimentos();
        this.verificarPodeEncerrarAtendimento();
        this.atendimentoTransferido.emit({
          contatoId: contato.id,
          canal: resultado.canalTransferencia,
          operador: resultado.operadorTransferencia
        });
      }
    });
  }
}
